import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, TrendingUp, DollarSign, Home, Trash2, Edit2, X, Check, Plus, BarChart3, Crown, Lock, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { API, formatCurrency } from "../../lib/helpers";

const SummaryCard = ({ label, value, color = "text-foreground", prefix = "" }) => (
  <div className="p-3 rounded-lg bg-card border border-border">
    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
    <p className={`font-mono text-base sm:text-lg font-bold ${color}`}>
      {prefix}{typeof value === "number" ? formatCurrency(value) : value}
    </p>
  </div>
);

const PortfolioPropertyCard = ({ item, onDelete, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    monthly_rent: item.monthly_rent || 0,
    monthly_mortgage: item.monthly_mortgage || 0,
    monthly_expenses: item.monthly_expenses || 0,
    current_value: item.current_value || item.purchase_price,
    notes: item.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const cashFlow = (item.monthly_rent || 0) - (item.monthly_mortgage || 0) - (item.monthly_expenses || 0);
  const equity = (item.current_value || item.purchase_price) - (item.purchase_price * 0.8);
  const appreciation = item.current_value && item.purchase_price
    ? (((item.current_value - item.purchase_price) / item.purchase_price) * 100).toFixed(1)
    : "0.0";

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portfolio/${item.portfolio_id}`, form);
      onUpdate(item.portfolio_id, form);
      setEditing(false);
      toast.success("Portfolio updated");
    } catch {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border overflow-hidden" data-testid={`portfolio-card-${item.portfolio_id}`}>
      <div className="flex flex-col sm:flex-row">
        {item.image_url && (
          <div className="w-full sm:w-40 h-32 sm:h-auto flex-shrink-0 overflow-hidden">
            <img src={item.image_url} alt={item.address} className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-semibold text-sm text-foreground flex items-center gap-1.5 flex-wrap">
                <span>{item.address}</span>
                {(() => {
                  const zillowHref = item.listing_url || (item.address ? `https://www.zillow.com/homes/${encodeURIComponent([item.address, item.city, `${item.state || ""} ${item.zip_code || ""}`.trim()].filter(Boolean).join(", "))}_rb/` : "");
                  return zillowHref ? (
                    <a
                      href={zillowHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-blue-500 hover:text-blue-400"
                      title="View on Zillow"
                      data-testid={`portfolio-zillow-link-${item.portfolio_id}`}
                    >
                      Zillow<ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  ) : null;
                })()}
              </h3>
              <p className="text-xs text-muted-foreground">{item.city}, {item.state} {item.zip_code}</p>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setEditing(!editing)}
                data-testid={`portfolio-edit-${item.portfolio_id}`}
              >
                {editing ? <X className="w-3.5 h-3.5" /> : <Edit2 className="w-3.5 h-3.5" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                onClick={() => onDelete(item.portfolio_id)}
                data-testid={`portfolio-delete-${item.portfolio_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-3">
            <Badge variant="secondary" className="text-[10px]">{item.property_type}</Badge>
            {item.bedrooms > 0 && <Badge variant="secondary" className="text-[10px]">{item.bedrooms}bd/{item.bathrooms}ba</Badge>}
            {item.sqft > 0 && <Badge variant="secondary" className="text-[10px]">{item.sqft.toLocaleString()} sqft</Badge>}
            <Badge variant="secondary" className="text-[10px]">Bought {item.purchase_date}</Badge>
          </div>

          {editing ? (
            <div className="space-y-3 p-3 rounded-lg bg-secondary/30 border border-border">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground">Monthly Rent</label>
                  <Input type="number" value={form.monthly_rent} onChange={e => setForm(f => ({ ...f, monthly_rent: Number(e.target.value) }))} className="h-7 text-xs font-mono mt-0.5" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Mortgage/mo</label>
                  <Input type="number" value={form.monthly_mortgage} onChange={e => setForm(f => ({ ...f, monthly_mortgage: Number(e.target.value) }))} className="h-7 text-xs font-mono mt-0.5" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Expenses/mo</label>
                  <Input type="number" value={form.monthly_expenses} onChange={e => setForm(f => ({ ...f, monthly_expenses: Number(e.target.value) }))} className="h-7 text-xs font-mono mt-0.5" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Current Value</label>
                  <Input type="number" value={form.current_value} onChange={e => setForm(f => ({ ...f, current_value: Number(e.target.value) }))} className="h-7 text-xs font-mono mt-0.5" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-7 text-xs mt-0.5" placeholder="Private notes..." />
              </div>
              <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`portfolio-save-${item.portfolio_id}`}>
                <Check className="w-3 h-3 mr-1" />{saving ? "Saving..." : "Save"}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div>
                <p className="text-[9px] text-muted-foreground uppercase">Purchase</p>
                <p className="font-mono text-xs font-bold">{formatCurrency(item.purchase_price)}</p>
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground uppercase">Current Value</p>
                <p className="font-mono text-xs font-bold">{formatCurrency(item.current_value || item.purchase_price)}</p>
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground uppercase">Rent/mo</p>
                <p className="font-mono text-xs font-bold text-emerald-500">{formatCurrency(item.monthly_rent || 0)}</p>
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground uppercase">Cash Flow/mo</p>
                <p className={`font-mono text-xs font-bold ${cashFlow >= 0 ? "text-emerald-500" : "text-red-500"}`}>{formatCurrency(cashFlow)}</p>
              </div>
              <div>
                <p className="text-[9px] text-muted-foreground uppercase">Appreciation</p>
                <p className={`font-mono text-xs font-bold ${parseFloat(appreciation) >= 0 ? "text-emerald-500" : "text-red-500"}`}>{appreciation}%</p>
              </div>
            </div>
          )}

          {item.notes && !editing && (
            <p className="mt-2 text-xs text-muted-foreground italic">{item.notes}</p>
          )}
        </div>
      </div>
    </Card>
  );
};

export default function PortfolioTab() {
  const [portfolio, setPortfolio] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isElite, setIsElite] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPortfolio = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`);
      setPortfolio(res.data.properties);
      setSummary(res.data.summary);
      setIsElite(!!res.data.is_elite);
    } catch {
      toast.error("Failed to load portfolio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPortfolio(); }, []);

  const handleDelete = async (portfolioId) => {
    try {
      await axios.delete(`${API}/portfolio/${portfolioId}`);
      toast.success("Removed from portfolio");
      fetchPortfolio();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const handleUpdate = (portfolioId, updates) => {
    setPortfolio(prev => prev.map(p =>
      p.portfolio_id === portfolioId ? { ...p, ...updates } : p
    ));
    fetchPortfolio();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="portfolio-loading">
        <div className="text-sm text-muted-foreground animate-pulse">Loading portfolio...</div>
      </div>
    );
  }

  if (portfolio.length === 0) {
    // Non-Elite users see a full-value Elite marketing pitch. Existing
    // Elite subscribers see the softer empty state that just nudges them
    // to add their first property from an analysis.
    if (!isElite) {
      return (
        <div className="max-w-3xl mx-auto py-8" data-testid="portfolio-elite-paywall">
          <Card className="relative overflow-hidden border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-background to-emerald-500/5">
            <div className="absolute top-0 left-0 right-0 bg-amber-500 text-black text-center text-xs font-bold py-1 uppercase tracking-wider">
              <Crown className="w-3 h-3 inline mr-1 -mt-0.5" /> Elite feature · Portfolio Dashboard
            </div>
            <CardContent className="pt-12 pb-8 px-6 sm:px-10">
              <div className="text-center mb-6">
                <div className="inline-flex w-14 h-14 rounded-2xl bg-amber-500/20 items-center justify-center mb-3">
                  <Briefcase className="w-7 h-7 text-amber-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Manage your entire portfolio in one place
                </h3>
                <p className="text-sm text-muted-foreground max-w-lg mx-auto">
                  RealixData Pro helps you find the next deal. Elite helps you
                  track and grow the ones you&apos;ve already bought — with live
                  cash flow, equity, and appreciation across every property.
                </p>
              </div>

              {/* Preview of what they'll get */}
              <div className="rounded-xl border border-border bg-card/60 p-4 sm:p-5 mb-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[
                    { label: "Properties", value: "12" },
                    { label: "Total Value", value: "$3.84M" },
                    { label: "Monthly Cash Flow", value: "$9,420", color: "text-emerald-500" },
                    { label: "Avg Cap Rate", value: "7.1%" },
                  ].map((s) => (
                    <div key={s.label} className="p-3 rounded-lg bg-secondary/40 border border-border">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">
                        {s.label}
                      </p>
                      <p className={`font-mono text-base sm:text-lg font-bold ${s.color || "text-foreground"}`}>
                        {s.value}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground text-center italic border-t border-border pt-3">
                  Sample portfolio · Your real numbers appear here
                </div>
              </div>

              <div className="space-y-2.5 mb-7 max-w-md mx-auto">
                {[
                  "Aggregate cash flow, equity, and ROI across every property",
                  "Live current-value tracking with appreciation trends",
                  "Per-property P&L: rent · mortgage · taxes · expenses",
                  "One-click Add to Portfolio from any deal analysis",
                  "Everything in Pro plus Bulk Analyze & Custom Deal Scoring",
                ].map((f) => (
                  <div key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
                <Button
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 shadow-lg shadow-amber-500/30"
                  onClick={() => navigate("/pricing")}
                  data-testid="portfolio-upgrade-cta"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Elite · $39/mo
                </Button>
                <span className="text-xs text-muted-foreground">
                  Cancel anytime · 30-day money-back guarantee
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="text-center py-16" data-testid="portfolio-empty">
        <Briefcase className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-semibold mb-1">No Properties in Portfolio Yet</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Analyze a property, then click <b>Add to Portfolio</b> to start
          tracking it here.
        </p>
      </div>
    );
  }

  const cashFlowColor = summary?.total_monthly_cash_flow >= 0 ? "text-emerald-500" : "text-red-500";

  return (
    <div className="space-y-6" data-testid="portfolio-tab">
      {/* Legacy-user grandfathering notice — for non-Elite accounts that
          added properties before Feb-2026 repackaging. They keep their
          data but can no longer add/edit new entries. */}
      {!isElite && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
          data-testid="portfolio-legacy-notice"
        >
          <Crown className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-amber-300">
              Portfolio Dashboard is now an Elite feature.
            </p>
            <p className="text-[11px] text-amber-200/80 mt-0.5">
              We&apos;re keeping your existing {portfolio.length} propert{portfolio.length === 1 ? "y" : "ies"} readable — but adding or editing new ones requires Elite.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-black h-7 text-xs flex-shrink-0"
            onClick={() => navigate("/pricing")}
            data-testid="portfolio-legacy-upgrade-btn"
          >
            Upgrade
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          <SummaryCard label="Properties" value={summary.total_properties} />
          <SummaryCard label="Total Value" value={summary.total_value} />
          <SummaryCard label="Total Invested" value={summary.total_invested} />
          <SummaryCard label="Monthly Rent" value={summary.total_monthly_rent} color="text-emerald-500" />
          <SummaryCard label="Cash Flow/mo" value={summary.total_monthly_cash_flow} color={cashFlowColor} />
          <SummaryCard label="Annual Cash Flow" value={summary.total_annual_cash_flow} color={cashFlowColor} />
        </div>
      )}

      {/* Portfolio Value Chart */}
      {portfolio.length > 0 && (
        <Card className="border-border" data-testid="portfolio-chart">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-500" />
              <CardTitle className="text-sm font-semibold">Portfolio Value Breakdown</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-2">
              {portfolio.map((item) => {
                const val = item.current_value || item.purchase_price || 0;
                const maxVal = Math.max(...portfolio.map(p => p.current_value || p.purchase_price || 0));
                const pct = maxVal > 0 ? (val / maxVal * 100) : 0;
                const gain = val - (item.purchase_price || 0);
                return (
                  <div key={item.portfolio_id} className="flex items-center gap-3" data-testid={`chart-bar-${item.portfolio_id}`}>
                    <div className="w-28 sm:w-36 truncate text-xs text-muted-foreground flex-shrink-0">{item.address?.split(',')[0]}</div>
                    <div className="flex-1 bg-secondary/30 rounded-full h-5 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${gain >= 0 ? 'bg-emerald-500/70' : 'bg-red-500/70'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-24 text-right flex-shrink-0">
                      <span className="text-xs font-mono font-bold">{formatCurrency(val)}</span>
                      <span className={`text-[9px] font-mono ml-1 ${gain >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {gain >= 0 ? '+' : ''}{formatCurrency(gain)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {summary && (
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Total Portfolio Value</span>
                <span className="text-base font-mono font-bold text-emerald-500">{formatCurrency(summary.total_value)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Property Cards */}
      <div className="space-y-3">
        {portfolio.map((item) => (
          <PortfolioPropertyCard
            key={item.portfolio_id}
            item={item}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
          />
        ))}
      </div>
    </div>
  );
}
